import { useCallback, useEffect, useRef } from "react";

import { createMentionToast, createMentionToastWithVisitGroupCta } from "components/shared/toasts";
import { useCapabilities, useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUserProfileProperties, useUsersProfileProperties } from "hooks/use_user_properties";
import { visitBreakoutRoom } from "shared/reducers/classroom";
import { BreakoutRoomType, CapabilityType, SidebarPanelType } from "shared/types";
import { RoleType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { setHasNewChatMessages, setSidebarPanel } from "store/reducers/local";
import { selectActiveBreakoutRoomForUserId } from "store/selectors/classroom";
import { escapeRegex } from "utils/string";
import { longDisplayName } from "utils/user";

// Observes chat messages so toast notifications can be dispatched
// Separate from the chat sidebar since the chat sidebar isn't always visible
// and receiving events
function ChatMessageObserver() {
  const dispatch = useAppDispatch();
  const currentPanel = useAppSelector(localSelectors.selectSidebarPanel());
  const users = useUsersProfileProperties();

  // Current user info
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const user = useUserProfileProperties(currentUserId);
  const isCaptioner = user?.role === RoleType.Captioner;
  const { canControlClassroom, canMentionEveryone } = useCapabilities();

  // Get most recent chat message
  const chats = useArraySelector(classroomSelectors.selectChats(), ["id"]);
  const lastChat = chats[chats.length - 1];
  const sender = useUserProfileProperties(lastChat?.user_id);
  const senderCanMentionEveryone = useCapability(CapabilityType.CAN_MENTION_EVERYONE_IN_CHAT, {
    userId: sender?.id,
  });

  const isFirstRender = useRef(true);

  const handleClickToast = isCaptioner
    ? undefined
    : () => {
        dispatch(setSidebarPanel({ panel: SidebarPanelType.Chat }));
      };

  // Breakout room info
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const senderBreakoutRoom = useAppSelector(selectActiveBreakoutRoomForUserId(sender?.id));
  const userBreakoutRoom = useAppSelector(
    classroomSelectors.selectActiveBreakoutRoomForCurrentUser(),
  );
  const handleVisitBreakoutRoomClick = useCallback(
    (breakoutRoom: BreakoutRoomType) => () => {
      dispatch(visitBreakoutRoom({ userId: currentUserId, breakoutRoomId: breakoutRoom.id }));
    },
    [currentUserId, dispatch],
  );

  useEffect(() => {
    // Reset new chat message indicator when chat panel is opened
    if (currentPanel === SidebarPanelType.Chat) {
      dispatch(setHasNewChatMessages(false));
    }
  }, [dispatch, currentPanel]);

  useEffect(() => {
    if (isFirstRender.current) {
      // Don't show toast and new chat message indicator on first render
      dispatch(setHasNewChatMessages(false));
      isFirstRender.current = false;
      return;
    }

    const senderId = lastChat?.user_id;
    if (!sender || !user || !lastChat || senderId === currentUserId) {
      return;
    }

    if (currentPanel !== SidebarPanelType.Chat) {
      // Show new chat message indicator when chat panel is closed and new message is received
      dispatch(setHasNewChatMessages(true));
    }

    // It is not enough to check that a tag is part of the message because it could be a substring of another mention (e.g. @Ally Billy contains @All).
    // Let's use regex instead: tags should be followed by a space or a punctuation mark or the end of the string
    const allRegex = /@all([.,!?;:()[\]{}'"-]|\s|$)/i;
    const everyoneMentioned = senderCanMentionEveryone && allRegex.test(lastChat?.text);

    const userRegex = new RegExp(
      `@${escapeRegex(longDisplayName(user))}([.,!?;:()[\\]{}'"-]|\\s|$)`,
      "i",
    );
    const userMentioned = userRegex.test(lastChat?.text);

    const captionerRegex = /@captioner([.,!?;:()[\]{}'"-]|\s|$)/i;
    const captionerMentioned = isCaptioner && captionerRegex.test(lastChat?.text);

    const breakoutGroupRegex =
      userBreakoutRoom &&
      new RegExp(`@group ${escapeRegex(userBreakoutRoom.title)}([.,!?;:()[\\]{}'"-]|\\s|$)`, "i");
    const breakoutGroupMentioned =
      activeBreakoutSessionId && userBreakoutRoom && breakoutGroupRegex?.test(lastChat?.text);

    const mentioned =
      everyoneMentioned || userMentioned || captionerMentioned || breakoutGroupMentioned;

    if (mentioned) {
      if (activeBreakoutSessionId && (canControlClassroom || isCaptioner)) {
        if (senderBreakoutRoom && senderBreakoutRoom.id !== userBreakoutRoom?.id) {
          createMentionToastWithVisitGroupCta(
            lastChat,
            users,
            canMentionEveryone,
            handleClickToast,
            senderBreakoutRoom.title,
            handleVisitBreakoutRoomClick(senderBreakoutRoom),
          );
        } else {
          createMentionToast(lastChat, users, canMentionEveryone, handleClickToast);
        }
      } else {
        createMentionToast(lastChat, users, canMentionEveryone, handleClickToast);
      }
    }
    // We only want to run this effect when the last chat message changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChat]);

  return null;
}

export { ChatMessageObserver };
